import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Form } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { CreateUpdateFiscalPrinterLocationDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateFiscalPrinterLocationDto';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';
import HealthcareFacilitySelector from '../Selects/HealthcareFacilitySelector';

interface FiscalPrinterLocationEditorProps {
  id?: string;
  location: CreateUpdateFiscalPrinterLocationDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    location: CreateUpdateFiscalPrinterLocationDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  onDeleted?: () => void;
}

export const FiscalPrinterLocationEditor: React.FC<
  FiscalPrinterLocationEditorProps
> = props => {
  const spd = useSpdCore();
  const [location, setLocation] = useState(props.location);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setLocation(props.location);
    }
  }, [props.show, props.location, props.id]);

  const propertyChange = (
    obj: Partial<CreateUpdateFiscalPrinterLocationDto>,
  ) => {
    setLocation({ ...location, ...obj });
  };

  const handleSave = async () => {
    props
      .onCompleted(location, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
        }
      })
      .catch(error => {
        setSaveErrors(error);
        setShowSaveErrors(true);
      });
  };

  const handleDelete = async () => {
    if (props.id) {
      try {
        await spd.fiscalPrinterLocations.delete(props.id);
        props.onDeleted?.();
      } catch (response) {
        const errors = await responseErrors(response as ResponseError);
        setSaveErrors(errors);
        setShowSaveErrors(true);
      }
    }
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id ? 'Edycja drukarki' : 'Dodawanie drukarki'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-2">
            <Card.Header>Drukarka fiskalna - placówka</Card.Header>
            <Card.Body>
              <Form className="d-grid gap-3">
                <FormRow controlId="printerUid" label="UID drukarki">
                  <Form.Control
                    type="text"
                    value={location?.printerUid || ''}
                    onChange={e =>
                      propertyChange({ printerUid: e.target.value })
                    }
                  />
                </FormRow>
                <FormRow controlId="healthcareFacility" label="Placówka">
                  <HealthcareFacilitySelector
                    value={location.healthcareFacilityId || undefined}
                    clearable
                    onChange={option =>
                      propertyChange({
                        healthcareFacilityId: option?.value || null,
                      })
                    }
                    placeholder="Wybierz placówkę..."
                  />
                </FormRow>
              </Form>
            </Card.Body>
          </Card>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Zamknij
          </Button>
          {props.id && (
            <ConfirmationButton
              className="mx-1"
              confirmation="Czy na pewno usunąć drukarkę?"
              variant="outline-danger"
              onOK={handleDelete}
            >
              Usuń
            </ConfirmationButton>
          )}
          <Button variant="outline-primary" onClick={handleSave}>
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
